import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Pause,
  CheckCircle,
  Archive,
  Plus,
  Trash2,
  Clock,
  Send,
  Mail,
  Users,
  BarChart3,
  GripVertical,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  UserPlus,
  Building2,
  Loader2,
  X,
  Pencil,
  Settings2,
} from 'lucide-react';
import { Button, Card, Modal, Select, Input, SearchInput, Skeleton, EmptyState } from '@solid/ui';
import { cn } from '@/lib/utils';
import {
  useSequence,
  useUpdateSequence,
  useDeleteSequence,
  useApproveSequence,
  usePauseSequence,
  useResumeSequence,
  useArchiveSequence,
  useAddStep,
  useUpdateStep,
  useRemoveStep,
  useSequenceStats,
  useSequenceEnrollments,
  useUnenrollContact,
  useEnrollContacts,
  useSendTestEmail,
  useUpdateEnrollmentVariables,
  useSendOneNow,
  useAddVariant,
  useUpdateVariant,
  useRemoveVariant,
  useVariantStats,
  useCreateDealFromEnrollment,
} from '@/hooks/useSequences';
import { useContacts } from '@/hooks/useContacts';
import { useDeals } from '@/hooks/useDeals';
import type { SequenceStatus, EnrollmentStatus, EmailSequenceStep } from '@/types';

const statusColors: Record<SequenceStatus, string> = {
  DRAFT: 'bg-grey-100 text-grey-700',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700',
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-blue-100 text-blue-700',
  ARCHIVED: 'bg-grey-100 text-grey-500',
};

const statusLabels: Record<SequenceStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  ARCHIVED: 'Archived',
};

const enrollmentStatusColors: Record<EnrollmentStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-grey-100 text-grey-700',
  BOUNCED: 'bg-red-100 text-red-700',
  REPLIED: 'bg-purple-100 text-purple-700',
  UNSUBSCRIBED: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-grey-100 text-grey-500',
};

function BccSettings({ sequence }: { sequence: any }) {
  const [editing, setEditing] = useState(false);
  const [emailsValue, setEmailsValue] = useState('');
  const updateMutation = useUpdateSequence();

  const startEditing = () => {
    setEmailsValue(sequence.bccEmails?.join(', ') || '');
    setEditing(true);
  };

  const handleSave = async () => {
    const emails = emailsValue.split(',').map((e: string) => e.trim()).filter(Boolean);
    await updateMutation.mutateAsync({
      id: sequence.id,
      data: { bccEmails: emails, bccEnabled: emails.length > 0 ? sequence.bccEnabled : false },
    });
    setEditing(false);
  };

  const handleToggle = async () => {
    await updateMutation.mutateAsync({
      id: sequence.id,
      data: { bccEnabled: !sequence.bccEnabled },
    });
  };

  return (
    <div className="flex items-center gap-3 text-sm">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={sequence.bccEnabled || false}
          onChange={handleToggle}
          disabled={!sequence.bccEmails?.length && !sequence.bccEnabled}
          className="rounded border-grey-300 text-primary focus:ring-primary/20"
        />
        <Mail className="w-3.5 h-3.5 text-grey-400" />
        <span className="text-xs font-medium text-grey-600">BCC on all emails</span>
      </label>
      {editing ? (
        <div className="flex items-center gap-2 flex-1">
          <input
            autoFocus
            placeholder="email1@example.com, email2@example.com"
            value={emailsValue}
            onChange={(e) => setEmailsValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            className="flex-1 px-2 py-1 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
          <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      ) : sequence.bccEmails?.length > 0 ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-grey-500">{sequence.bccEmails.join(', ')}</span>
          <button onClick={startEditing} className="text-xs text-primary hover:underline flex items-center gap-1">
            <Pencil className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button onClick={startEditing} className="text-xs text-grey-400 hover:text-primary">
          Add BCC emails
        </button>
      )}
    </div>
  );
}

export default function SequenceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: sequence, isLoading } = useSequence(id!);
  const { data: stats } = useSequenceStats(id!);
  const { data: enrollments } = useSequenceEnrollments(id!);
  const updateMutation = useUpdateSequence();
  const deleteMutation = useDeleteSequence();
  const approveMutation = useApproveSequence();
  const pauseMutation = usePauseSequence();
  const resumeMutation = useResumeSequence();
  const archiveMutation = useArchiveSequence();
  const addStepMutation = useAddStep();
  const unenrollMutation = useUnenrollContact();

  const [activeTab, setActiveTab] = useState<'steps' | 'enrollments' | 'stats'>('steps');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  if (isLoading) {
    return (
      <div className="page-enter space-y-6">
        <Skeleton className="h-12 rounded-xl w-1/3" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (!sequence) {
    return (
      <div className="page-enter text-center py-12">
        <p className="text-grey-500">Sequence not found</p>
      </div>
    );
  }

  const handleSaveName = async () => {
    if (nameValue.trim() && nameValue !== sequence.name) {
      await updateMutation.mutateAsync({ id: sequence.id, data: { name: nameValue } });
    }
    setEditingName(false);
  };

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this sequence?')) {
      await deleteMutation.mutateAsync(sequence.id);
      navigate('/admin/sequences');
    }
  };

  const handleAddStep = async () => {
    await addStepMutation.mutateAsync({
      sequenceId: sequence.id,
      data: {
        position: sequence.steps.length,
        subject: 'New step',
        body: '<p>Email content here...</p>',
        delayDays: sequence.steps.length === 0 ? 0 : 1,
        delayHours: 0,
      },
    });
  };

  return (
    <div className="page-enter space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/sequences')}
          className="p-2 rounded-lg hover:bg-grey-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-grey-500" />
        </button>
        <div className="flex-1">
          {editingName ? (
            <input
              autoFocus
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
              className="text-heading-2 text-secondary bg-transparent border-b-2 border-primary outline-none w-full"
            />
          ) : (
            <h1
              className="text-heading-2 text-secondary cursor-pointer hover:text-primary transition-colors"
              onClick={() => {
                setNameValue(sequence.name);
                setEditingName(true);
              }}
            >
              {sequence.name}
            </h1>
          )}
          {sequence.description && (
            <p className="text-body-sm text-grey-500 mt-1">{sequence.description}</p>
          )}
        </div>
        <span className={cn('px-3 py-1.5 rounded-full text-xs font-medium', statusColors[sequence.status])}>
          {statusLabels[sequence.status]}
        </span>
      </div>

      {/* BCC Settings */}
      <BccSettings sequence={sequence} />

      {/* Actions */}
      <div className="flex items-center gap-2">
        {(sequence.status === 'DRAFT' || sequence.status === 'PENDING_APPROVAL') && (
          <Button
            onClick={() => approveMutation.mutate(sequence.id)}
            loading={approveMutation.isPending}
            disabled={sequence.steps.length === 0}
          >
            <CheckCircle className="w-4 h-4" />
            Approve & Activate
          </Button>
        )}
        {sequence.status === 'ACTIVE' && (
          <Button
            variant="outline"
            onClick={() => pauseMutation.mutate(sequence.id)}
            loading={pauseMutation.isPending}
          >
            <Pause className="w-4 h-4" />
            Pause
          </Button>
        )}
        {sequence.status === 'PAUSED' && (
          <Button
            onClick={() => resumeMutation.mutate(sequence.id)}
            loading={resumeMutation.isPending}
          >
            <Play className="w-4 h-4" />
            Resume
          </Button>
        )}
        {sequence.status !== 'ARCHIVED' && (
          <Button
            variant="ghost"
            onClick={() => archiveMutation.mutate(sequence.id)}
            loading={archiveMutation.isPending}
          >
            <Archive className="w-4 h-4" />
            Archive
          </Button>
        )}
        {sequence.status === 'DRAFT' && (
          <Button
            variant="ghost"
            className="text-error hover:text-error"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-grey-200 pb-px">
        {[
          { key: 'steps', label: 'Steps', icon: Mail, count: sequence.steps.length },
          { key: 'enrollments', label: 'Enrollments', icon: Users, count: enrollments?.length },
          { key: 'stats', label: 'Stats', icon: BarChart3 },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-grey-500 hover:text-secondary',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1 text-xs bg-grey-100 text-grey-600 px-1.5 rounded-full">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'steps' && (
        <StepsTab
          steps={sequence.steps}
          sequenceId={sequence.id}
          isEditable={sequence.status !== 'ARCHIVED'}
          onAddStep={handleAddStep}
          isAddingStep={addStepMutation.isPending}
        />
      )}

      {activeTab === 'enrollments' && (
        <EnrollmentsTab
          enrollments={enrollments || []}
          sequenceId={sequence.id}
          onUnenroll={(contactId) => unenrollMutation.mutate({ id: sequence.id, contactId })}
        />
      )}

      {activeTab === 'stats' && <StatsTab stats={stats} />}
    </div>
  );
}

// ============================================
// Steps Tab
// ============================================

function StepsTab({
  steps,
  isEditable,
  onAddStep,
  isAddingStep,
}: {
  steps: EmailSequenceStep[];
  sequenceId: string;
  isEditable: boolean;
  onAddStep: () => void;
  isAddingStep: boolean;
}) {
  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <StepCard key={step.id} step={step} index={index} isEditable={isEditable} isLast={index === steps.length - 1} />
      ))}

      {isEditable && (
        <button
          onClick={onAddStep}
          disabled={isAddingStep}
          className="w-full border-2 border-dashed border-grey-200 rounded-xl p-6 text-grey-400 hover:border-primary/30 hover:text-primary transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Step
        </button>
      )}
    </div>
  );
}

function StepCard({
  step,
  index,
  isEditable,
  isLast,
}: {
  step: EmailSequenceStep;
  index: number;
  isEditable: boolean;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const updateMutation = useUpdateStep();
  const removeMutation = useRemoveStep();
  const sendTestMutation = useSendTestEmail();
  const addVariantMutation = useAddVariant();
  const updateVariantMutation = useUpdateVariant();
  const removeVariantMutation = useRemoveVariant();
  const [delayDays, setDelayDays] = useState(step.delayDays);
  const [delayHours, setDelayHours] = useState(step.delayHours);
  const [showTestForm, setShowTestForm] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSuccess, setTestSuccess] = useState(false);
  const [testVars, setTestVars] = useState<Array<{ key: string; value: string }>>([]);

  const variants = step.variants || [];
  const hasMultipleVariants = variants.length > 1;
  const [activeVariantLabel, setActiveVariantLabel] = useState('A');

  const activeVariant = variants.find((v) => v.label === activeVariantLabel) || variants[0];

  // Local editing state for variant subject/body
  const [varSubject, setVarSubject] = useState(activeVariant?.subject || step.subject);
  const [varBody, setVarBody] = useState(activeVariant?.body || step.body);
  const [varWeight, setVarWeight] = useState(activeVariant?.weight || 100);

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  const handleSave = async () => {
    try {
      if (activeVariant && hasMultipleVariants) {
        await updateVariantMutation.mutateAsync({
          variantId: activeVariant.id,
          data: { subject: varSubject, body: varBody, weight: varWeight },
        });
      } else {
        // Single variant or no variants — update step directly
        await updateMutation.mutateAsync({
          stepId: step.id,
          data: { subject: varSubject, body: varBody, delayDays, delayHours },
        });
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handleSaveDelay = async () => {
    await updateMutation.mutateAsync({
      stepId: step.id,
      data: { delayDays, delayHours },
    });
  };

  const handleAddVariant = async () => {
    await addVariantMutation.mutateAsync({
      stepId: step.id,
      data: { subject: step.subject, body: step.body },
    });
  };

  const handleRemoveVariant = async (variantId: string) => {
    await removeVariantMutation.mutateAsync(variantId);
    setActiveVariantLabel('A');
  };

  const delay = step.delayDays > 0 || step.delayHours > 0
    ? `${step.delayDays > 0 ? `${step.delayDays}d` : ''}${step.delayHours > 0 ? ` ${step.delayHours}h` : ''}`.trim()
    : 'Immediately';

  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);

  return (
    <div className="relative">
      {!isLast && (
        <div className="absolute left-6 top-full w-0.5 h-4 bg-grey-200 z-0" />
      )}

      <Card padding="md">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          {isEditable && <GripVertical className="w-4 h-4 text-grey-300 flex-shrink-0" />}
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-primary">{index + 1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-secondary truncate">{step.subject}</p>
            <div className="flex items-center gap-2 text-xs text-grey-500">
              <Clock className="w-3 h-3" />
              <span>{delay}</span>
              {step.replyTo && index > 0 && (
                <>
                  <span className="text-grey-300">·</span>
                  <span>Reply in thread</span>
                </>
              )}
              {hasMultipleVariants && (
                <>
                  <span className="text-grey-300">·</span>
                  <span>{variants.length} variants</span>
                </>
              )}
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-grey-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-grey-400" />
          )}
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-grey-100 space-y-4">
            {/* Variant tabs */}
            {variants.length > 0 && (
              <div className="flex items-center gap-1">
                {variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => {
                      setActiveVariantLabel(v.label);
                      setVarSubject(v.subject);
                      setVarBody(v.body);
                      setVarWeight(v.weight);
                    }}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 transition-colors',
                      activeVariantLabel === v.label
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-grey-500 hover:text-secondary hover:bg-grey-50',
                    )}
                  >
                    Variant {v.label}
                    {hasMultipleVariants && totalWeight > 0 && (
                      <span className="ml-1 text-grey-400">
                        {Math.round((v.weight / totalWeight) * 100)}%
                      </span>
                    )}
                  </button>
                ))}
                {isEditable && variants.length < 4 && (
                  <button
                    onClick={handleAddVariant}
                    disabled={addVariantMutation.isPending}
                    className="px-2 py-1.5 text-xs text-grey-400 hover:text-primary transition-colors"
                    aria-label="Add variant"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {isEditable ? (
              <>
                <div>
                  <label htmlFor={`var-subject-${activeVariant?.id || step.id}`} className="block text-xs font-medium text-grey-600 mb-1">Subject</label>
                  <input
                    id={`var-subject-${activeVariant?.id || step.id}`}
                    value={varSubject}
                    onChange={(e) => setVarSubject(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor={`var-body-${activeVariant?.id || step.id}`} className="block text-xs font-medium text-grey-600 mb-1">
                    Body <span className="text-grey-400 font-normal">({'{{first_name}}'}, {'{{last_name}}'}, {'{{company_name}}'}, {'{{job_title}}'}, {'{{email}}'}, {'{{domain}}'}, {'{{website}}'}, {'{{custom_variable}}'})</span>
                  </label>
                  <textarea
                    id={`var-body-${activeVariant?.id || step.id}`}
                    value={varBody}
                    onChange={(e) => setVarBody(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
                  />
                </div>
                <div className="flex gap-4">
                  <div>
                    <label htmlFor={`step-delay-days-${step.id}`} className="block text-xs font-medium text-grey-600 mb-1">Delay (days)</label>
                    <input
                      id={`step-delay-days-${step.id}`}
                      type="number"
                      min={0}
                      value={delayDays}
                      onChange={(e) => setDelayDays(Number(e.target.value))}
                      onBlur={handleSaveDelay}
                      className="w-24 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label htmlFor={`step-delay-hours-${step.id}`} className="block text-xs font-medium text-grey-600 mb-1">Delay (hours)</label>
                    <input
                      id={`step-delay-hours-${step.id}`}
                      type="number"
                      min={0}
                      value={delayHours}
                      onChange={(e) => setDelayHours(Number(e.target.value))}
                      onBlur={handleSaveDelay}
                      className="w-24 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                  {hasMultipleVariants && (
                    <div>
                      <label htmlFor={`var-weight-${activeVariant?.id}`} className="block text-xs font-medium text-grey-600 mb-1">Weight</label>
                      <input
                        id={`var-weight-${activeVariant?.id}`}
                        type="number"
                        min={1}
                        value={varWeight}
                        onChange={(e) => setVarWeight(Number(e.target.value))}
                        className="w-24 px-3 py-2 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={handleSave}
                      loading={updateMutation.isPending || updateVariantMutation.isPending}
                    >
                      Save {hasMultipleVariants ? `Variant ${activeVariantLabel}` : 'Step'}
                    </Button>
                    {saveStatus === 'saved' && <span className="text-xs text-green-600">Saved!</span>}
                    {saveStatus === 'error' && <span className="text-xs text-red-600">Save failed — check console</span>}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowTestForm(!showTestForm); setTestSuccess(false); }}
                    >
                      <Send className="w-3.5 h-3.5" />
                      Send Test
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasMultipleVariants && activeVariant && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-error hover:text-error"
                        onClick={() => handleRemoveVariant(activeVariant.id)}
                        loading={removeVariantMutation.isPending}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Remove Variant
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-error hover:text-error"
                      onClick={() => removeMutation.mutate(step.id)}
                      loading={removeMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove Step
                    </Button>
                  </div>
                </div>

                {/* Add variant link for single-variant steps */}
                {!hasMultipleVariants && variants.length < 4 && (
                  <button
                    onClick={handleAddVariant}
                    disabled={addVariantMutation.isPending}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Add Variant B for A/B testing
                  </button>
                )}
              </>
            ) : (
              <div>
                <p className="text-xs font-medium text-grey-600 mb-1">Subject</p>
                <p className="text-sm text-secondary mb-3">{activeVariant?.subject || step.subject}</p>
                <p className="text-xs font-medium text-grey-600 mb-1">Body</p>
                <div
                  className="text-sm text-grey-700 bg-grey-50 rounded-lg p-3 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: activeVariant?.body || step.body }}
                />
                <div className="mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowTestForm(!showTestForm); setTestSuccess(false); }}
                  >
                    <Send className="w-3.5 h-3.5" />
                    Send Test
                  </Button>
                </div>
              </div>
            )}

            {/* Variant stats bar */}
            {hasMultipleVariants && <VariantStatsBar stepId={step.id} />}

            {showTestForm && (
              <div className="mt-3 pt-3 border-t border-grey-100 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    placeholder="recipient@example.com"
                    value={testEmail}
                    onChange={(e) => { setTestEmail(e.target.value); setTestSuccess(false); }}
                    className="flex-1 px-3 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!testEmail) return;
                      const vars: Record<string, string> = {};
                      testVars.forEach((v) => { if (v.key.trim()) vars[v.key.trim()] = v.value; });
                      try {
                        await sendTestMutation.mutateAsync({
                          stepId: step.id,
                          recipientEmail: testEmail,
                          testVariables: Object.keys(vars).length > 0 ? vars : undefined,
                          variantId: hasMultipleVariants ? activeVariant?.id : undefined,
                        });
                        setTestSuccess(true);
                      } catch {
                        // error handled by mutation state
                      }
                    }}
                    loading={sendTestMutation.isPending}
                    disabled={!testEmail}
                  >
                    Send{hasMultipleVariants ? ` (${activeVariantLabel})` : ''}
                  </Button>
                </div>
                {testVars.map((v, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      placeholder="variable_name"
                      value={v.key}
                      onChange={(e) => { const next = [...testVars]; next[i] = { ...next[i], key: e.target.value }; setTestVars(next); }}
                      className="w-36 px-2 py-1 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
                    />
                    <span className="text-xs text-grey-400">=</span>
                    <input
                      placeholder="value"
                      value={v.value}
                      onChange={(e) => { const next = [...testVars]; next[i] = { ...next[i], value: e.target.value }; setTestVars(next); }}
                      className="flex-1 px-2 py-1 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <button onClick={() => setTestVars(testVars.filter((_, j) => j !== i))} className="text-grey-400 hover:text-error">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setTestVars([...testVars, { key: '', value: '' }])}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add test variable
                </button>
                {testSuccess && (
                  <p className="text-xs text-green-600 mt-1.5">Test email sent successfully!</p>
                )}
                {sendTestMutation.isError && (
                  <p className="text-xs text-error mt-1.5">
                    {(sendTestMutation.error as any)?.response?.data?.message || 'Failed to send test email'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function VariantStatsBar({ stepId }: { stepId: string }) {
  const { data: stats } = useVariantStats(stepId);
  if (!stats || stats.length === 0) return null;

  return (
    <div className="flex items-center gap-4 px-3 py-2 bg-grey-50 rounded-lg text-xs">
      {stats.map((s) => (
        <div key={s.variantId} className="flex items-center gap-3">
          <span className="font-medium text-secondary">Variant {s.label}</span>
          <span className="text-grey-500">{s.sent} sent</span>
          <span className="text-amber-600">
            {s.opened} opened
            {s.sent > 0 && ` (${Math.round((s.opened / s.sent) * 100)}%)`}
          </span>
          <span className="text-teal-600">
            {s.clicked} clicked
            {s.sent > 0 && ` (${Math.round((s.clicked / s.sent) * 100)}%)`}
          </span>
          <span className="text-purple-600">{s.replied} replied</span>
          <span className="text-green-600">
            {s.converted} converted
            {s.sent > 0 && ` (${Math.round((s.converted / s.sent) * 100)}%)`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Enrollments Tab
// ============================================

function EnrollmentsTab({
  enrollments,
  sequenceId,
  onUnenroll,
}: {
  enrollments: any[];
  sequenceId: string;
  onUnenroll: (contactId: string) => void;
}) {
  const [showEnroll, setShowEnroll] = useState(false);
  const [subTab, setSubTab] = useState<'active' | 'completed'>('active');
  const [collapsedSteps, setCollapsedSteps] = useState<Set<number>>(new Set());

  const activeEnrollments = enrollments.filter((e: any) => e.status === 'ACTIVE');
  const completedEnrollments = enrollments.filter((e: any) => e.status !== 'ACTIVE');

  const displayed = subTab === 'active' ? activeEnrollments : completedEnrollments;

  // Group enrollments by currentStep
  const groupedByStep = useMemo(() => {
    const groups = new Map<number, any[]>();
    for (const enrollment of displayed) {
      const step = enrollment.currentStep ?? 0;
      if (!groups.has(step)) groups.set(step, []);
      groups.get(step)!.push(enrollment);
    }
    // Sort by step number
    return new Map([...groups.entries()].sort((a, b) => a[0] - b[0]));
  }, [displayed]);

  const toggleStep = (step: number) => {
    setCollapsedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(step)) next.delete(step);
      else next.add(step);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSubTab('active')}
            className={cn(
              'text-sm font-medium pb-0.5 border-b-2 transition-colors',
              subTab === 'active' ? 'border-primary text-primary' : 'border-transparent text-grey-500 hover:text-secondary',
            )}
          >
            Active ({activeEnrollments.length})
          </button>
          <button
            onClick={() => setSubTab('completed')}
            className={cn(
              'text-sm font-medium pb-0.5 border-b-2 transition-colors',
              subTab === 'completed' ? 'border-primary text-primary' : 'border-transparent text-grey-500 hover:text-secondary',
            )}
          >
            Completed ({completedEnrollments.length})
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowEnroll(true)}>
            <UserPlus className="w-4 h-4" />
            Enroll Contacts
          </Button>
        </div>
      </div>

      {displayed.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<Users />}
            title={subTab === 'active' ? 'No active enrollments' : 'No completed enrollments'}
            description={
              subTab === 'active'
                ? 'Click "Enroll Contacts" above to add contacts to this sequence'
                : undefined
            }
          />
        </Card>
      ) : (
        Array.from(groupedByStep.entries()).map(([step, stepEnrollments]) => {
          const isCollapsed = collapsedSteps.has(step);
          return (
            <Card key={step} padding="none" className="overflow-hidden">
              <button
                onClick={() => toggleStep(step)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-grey-50 hover:bg-grey-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-grey-400" />
                  )}
                  <span className="text-sm font-medium text-secondary">
                    Step {step + 1}
                  </span>
                  <span className="text-xs text-grey-500">
                    ({stepEnrollments.length} enrollment{stepEnrollments.length !== 1 ? 's' : ''})
                  </span>
                </div>
              </button>
              {!isCollapsed && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-grey-100 text-xs text-grey-500">
                      <th className="text-left font-medium px-4 py-2">Name</th>
                      <th className="text-left font-medium px-4 py-2">Email</th>
                      <th className="text-left font-medium px-4 py-2">Status</th>
                      <th className="text-left font-medium px-4 py-2">Deal</th>
                      <th className="text-right font-medium px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stepEnrollments.map((enrollment: any) => (
                      <EnrollmentRow
                        key={enrollment.id}
                        enrollment={enrollment}
                        onUnenroll={onUnenroll}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          );
        })
      )}

      <EnrollContactsDialog
        open={showEnroll}
        onOpenChange={setShowEnroll}
        sequenceId={sequenceId}
        enrolledContactIds={new Set(enrollments.map((e: any) => e.contactId))}
      />
    </div>
  );
}

function EnrollmentRow({
  enrollment,
  onUnenroll,
}: {
  enrollment: any;
  onUnenroll: (contactId: string) => void;
}) {
  const [editingVars, setEditingVars] = useState(false);
  const [varRows, setVarRows] = useState<Array<{ key: string; value: string }>>([]);
  const [showDealDialog, setShowDealDialog] = useState(false);
  const updateVarsMutation = useUpdateEnrollmentVariables();
  const sendOneNow = useSendOneNow();

  // Find the next SCHEDULED execution for this enrollment
  const nextExecution = enrollment.executions?.find((e: any) => e.status === 'SCHEDULED');

  const variables = enrollment.variables && typeof enrollment.variables === 'object'
    ? (enrollment.variables as Record<string, string>)
    : {};

  const startEditing = () => {
    const entries = Object.entries(variables);
    setVarRows(entries.length > 0 ? entries.map(([key, value]) => ({ key, value })) : [{ key: '', value: '' }]);
    setEditingVars(true);
  };

  const handleSaveVars = async () => {
    const vars: Record<string, string> = {};
    varRows.forEach((v) => { if (v.key.trim()) vars[v.key.trim()] = v.value; });
    await updateVarsMutation.mutateAsync({ enrollmentId: enrollment.id, variables: vars });
    setEditingVars(false);
  };

  return (
    <>
      <tr className="border-b border-grey-50 hover:bg-grey-50/50">
        <td className="px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-primary">
                {enrollment.contact.firstName?.[0]}{enrollment.contact.lastName?.[0]}
              </span>
            </div>
            <span className="font-medium text-secondary truncate">
              {enrollment.contact.firstName} {enrollment.contact.lastName}
            </span>
          </div>
        </td>
        <td className="px-4 py-2 text-grey-600 truncate max-w-[200px]">
          {enrollment.contact.email}
        </td>
        <td className="px-4 py-2">
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-xs font-medium',
              enrollmentStatusColors[enrollment.status as EnrollmentStatus],
            )}
          >
            {enrollment.status}
          </span>
        </td>
        <td className="px-4 py-2 text-grey-500 truncate max-w-[150px]">
          {enrollment.deal?.title || '\u2014'}
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center justify-end gap-1">
            {nextExecution && (
              <button
                onClick={() => sendOneNow.mutate(nextExecution.id)}
                className="p-1 text-grey-400 hover:text-green-600 rounded transition-colors disabled:opacity-50"
                aria-label="Send now"
                disabled={sendOneNow.isPending}
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            )}
            {!enrollment.deal && (
              <button
                onClick={() => setShowDealDialog(true)}
                className="p-1 text-grey-400 hover:text-green-600 rounded transition-colors"
                aria-label="Create deal"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={startEditing}
              className="p-1 text-grey-400 hover:text-primary rounded transition-colors"
              aria-label="Edit variables"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onUnenroll(enrollment.contactId)}
              className="p-1 text-grey-400 hover:text-error rounded transition-colors"
              aria-label="Remove from sequence"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </td>
      </tr>
      {editingVars && (
        <tr>
          <td colSpan={5} className="px-4 py-3 bg-grey-50/50">
            <div className="space-y-2">
              <p className="text-xs font-medium text-grey-600">Custom Variables</p>
              {varRows.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    placeholder="variable_name"
                    value={v.key}
                    onChange={(e) => { const next = [...varRows]; next[i] = { ...next[i], key: e.target.value }; setVarRows(next); }}
                    className="w-36 px-2 py-1 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
                  />
                  <span className="text-xs text-grey-400">=</span>
                  <input
                    placeholder="value"
                    value={v.value}
                    onChange={(e) => { const next = [...varRows]; next[i] = { ...next[i], value: e.target.value }; setVarRows(next); }}
                    className="flex-1 px-2 py-1 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <button onClick={() => setVarRows(varRows.filter((_, j) => j !== i))} className="text-grey-400 hover:text-error">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setVarRows([...varRows, { key: '', value: '' }])}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Variable
              </button>
              <div className="flex items-center gap-2 pt-1">
                <Button size="sm" onClick={handleSaveVars} loading={updateVarsMutation.isPending}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingVars(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {showDealDialog && (
        <tr>
          <td colSpan={5} className="p-0">
            <CreateDealFromEnrollmentDialog
              open={showDealDialog}
              onOpenChange={setShowDealDialog}
              enrollment={enrollment}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ============================================
// Create Deal from Enrollment Dialog
// ============================================

function CreateDealFromEnrollmentDialog({
  open,
  onOpenChange,
  enrollment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollment: any;
}) {
  const contactName = `${enrollment.contact.firstName || ''} ${enrollment.contact.lastName || ''}`.trim();
  const [title, setTitle] = useState(contactName || 'New Deal');
  const [value, setValue] = useState('');
  const createDealMutation = useCreateDealFromEnrollment();

  const handleSubmit = async () => {
    await createDealMutation.mutateAsync({
      enrollmentId: enrollment.id,
      data: {
        title,
        ...(value ? { value: Number(value) } : {}),
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Create Deal from Enrollment"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!title.trim()}
            loading={createDealMutation.isPending}
          >
            Create Deal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          id="deal-title"
          label="Deal Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Input
          id="deal-value"
          label="Value (optional)"
          type="number"
          min={0}
          placeholder="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <p className="text-xs text-grey-500">
          Deal will be created in the Sequence pipeline at Engaged stage.
        </p>
        {createDealMutation.isError && (
          <p className="text-xs text-error">
            {(createDealMutation.error as any)?.response?.data?.message || 'Failed to create deal'}
          </p>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// Enroll Contacts Dialog
// ============================================

function EnrollContactsDialog({
  open,
  onOpenChange,
  sequenceId,
  enrolledContactIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sequenceId: string;
  enrolledContactIds: Set<string>;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dealId, setDealId] = useState('');
  const [enrollVars, setEnrollVars] = useState<Array<{ key: string; value: string }>>([]);
  const [newContacts, setNewContacts] = useState<Array<{ firstName: string; lastName: string; email: string; jobTitle: string }>>([]);

  const { data: contactsData, isLoading: contactsLoading } = useContacts(
    open ? { search: search || undefined, take: 20 } : undefined,
  );
  const { data: dealsData } = useDeals(open ? { take: 50 } : undefined);
  const enrollMutation = useEnrollContacts();

  const contacts = contactsData?.data || [];
  const deals = dealsData?.data || [];

  const dealOptions = useMemo(
    () => deals.map((d) => ({ value: d.id, label: d.title })),
    [deals],
  );

  const toggleContact = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const validNewContacts = newContacts.filter((c) => c.email.trim() && c.firstName.trim() && c.lastName.trim());
  const totalToEnroll = selected.size + validNewContacts.length;

  const handleEnroll = async () => {
    const vars: Record<string, string> = {};
    enrollVars.forEach((v) => { if (v.key.trim()) vars[v.key.trim()] = v.value; });
    await enrollMutation.mutateAsync({
      id: sequenceId,
      data: {
        ...(selected.size > 0 ? { contactIds: Array.from(selected) } : {}),
        ...(validNewContacts.length > 0 ? { newContacts: validNewContacts.map(({ firstName, lastName, email, jobTitle }) => ({ firstName, lastName, email, ...(jobTitle ? { jobTitle } : {}) })) } : {}),
        ...(dealId ? { dealId } : {}),
        ...(Object.keys(vars).length > 0 ? { variables: vars } : {}),
      },
    });
    handleClose();
  };

  const handleClose = () => {
    onOpenChange(false);
    setSearch('');
    setSelected(new Set());
    setDealId('');
    setEnrollVars([]);
    setNewContacts([]);
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title="Enroll Contacts"
      size="md"
      footer={
        <div className="flex items-center justify-between w-full">
          <p className="text-sm text-grey-500">
            {totalToEnroll} {totalToEnroll === 1 ? 'contact' : 'contacts'} selected
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleEnroll}
              disabled={totalToEnroll === 0}
              loading={enrollMutation.isPending}
            >
              <UserPlus className="w-4 h-4" />
              Enroll {totalToEnroll > 0 ? `(${totalToEnroll})` : ''}
            </Button>
          </div>
        </div>
      }
    >
        <div className="space-y-4">
          {/* Search */}
          <SearchInput searches="contacts" value={search} onChange={setSearch} />

          {/* Contact list */}
          <div className="border border-grey-200 rounded-lg max-h-64 overflow-y-auto">
            {contactsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-grey-400" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-grey-500">
                  {search ? 'No contacts found' : 'Type to search contacts'}
                </p>
              </div>
            ) : (
              contacts.map((contact) => {
                const isEnrolled = enrolledContactIds.has(contact.id);
                const isSelected = selected.has(contact.id);

                return (
                  <label
                    key={contact.id}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 border-b border-grey-100 last:border-b-0 transition-colors',
                      isEnrolled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-grey-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isEnrolled}
                      onChange={() => toggleContact(contact.id)}
                      className="rounded border-grey-300 text-primary focus:ring-primary/20"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">
                        {contact.firstName} {contact.lastName}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-grey-500">
                        {contact.email && <span className="truncate">{contact.email}</span>}
                        {contact.organization && (
                          <>
                            <span className="text-grey-300">·</span>
                            <span className="flex items-center gap-1 truncate">
                              <Building2 className="w-3 h-3 flex-shrink-0" />
                              {contact.organization.name}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {isEnrolled && (
                      <span className="text-xs bg-grey-100 text-grey-500 px-2 py-0.5 rounded-full flex-shrink-0">
                        Already enrolled
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {/* New contacts */}
          <div>
            <p className="text-xs font-medium text-grey-600 mb-2">New Contacts</p>
            <div className="space-y-2">
              {newContacts.map((nc, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    placeholder="First name"
                    value={nc.firstName}
                    onChange={(e) => { const next = [...newContacts]; next[i] = { ...next[i], firstName: e.target.value }; setNewContacts(next); }}
                    className="w-28 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <input
                    placeholder="Last name"
                    value={nc.lastName}
                    onChange={(e) => { const next = [...newContacts]; next[i] = { ...next[i], lastName: e.target.value }; setNewContacts(next); }}
                    className="w-28 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <input
                    placeholder="Email"
                    type="email"
                    value={nc.email}
                    onChange={(e) => { const next = [...newContacts]; next[i] = { ...next[i], email: e.target.value }; setNewContacts(next); }}
                    className="flex-1 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <input
                    placeholder="Title"
                    value={nc.jobTitle}
                    onChange={(e) => { const next = [...newContacts]; next[i] = { ...next[i], jobTitle: e.target.value }; setNewContacts(next); }}
                    className="w-32 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <button onClick={() => setNewContacts(newContacts.filter((_, j) => j !== i))} className="text-grey-400 hover:text-error">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setNewContacts([...newContacts, { firstName: '', lastName: '', email: '', jobTitle: '' }])}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add New Contact
              </button>
            </div>
          </div>

          {/* Deal picker */}
          <Select
            label="Associate with deal (optional)"
            placeholder="No deal"
            options={dealOptions}
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
          />

          {/* Custom variables */}
          <div>
            <p className="text-xs font-medium text-grey-600 mb-2">Custom Variables (optional)</p>
            <div className="space-y-2">
              {enrollVars.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    placeholder="variable_name"
                    value={v.key}
                    onChange={(e) => { const next = [...enrollVars]; next[i] = { ...next[i], key: e.target.value }; setEnrollVars(next); }}
                    className="w-36 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
                  />
                  <span className="text-xs text-grey-400">=</span>
                  <input
                    placeholder="value"
                    value={v.value}
                    onChange={(e) => { const next = [...enrollVars]; next[i] = { ...next[i], value: e.target.value }; setEnrollVars(next); }}
                    className="flex-1 px-2 py-1.5 text-sm border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                  <button onClick={() => setEnrollVars(enrollVars.filter((_, j) => j !== i))} className="text-grey-400 hover:text-error">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setEnrollVars([...enrollVars, { key: '', value: '' }])}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Variable
              </button>
            </div>
          </div>

        </div>
    </Modal>
  );
}

// ============================================
// Stats Tab
// ============================================

function StatsTab({ stats }: { stats: any }) {
  if (!stats) {
    return (
      <Skeleton className="h-48 rounded-xl" />
    );
  }

  const statCards = [
    { label: 'Enrolled', value: stats.enrolled, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active', value: stats.active, icon: Play, color: 'text-green-600 bg-green-50' },
    { label: 'Sent', value: stats.sent, icon: Send, color: 'text-primary bg-primary/5' },
    { label: 'Opened', value: stats.opened, icon: Mail, color: 'text-amber-600 bg-amber-50', subtitle: stats.sent > 0 ? `${stats.openRate}%` : undefined },
    { label: 'Clicked', value: stats.clicked, icon: BarChart3, color: 'text-teal-600 bg-teal-50', subtitle: stats.sent > 0 ? `${stats.clickRate}%` : undefined },
    { label: 'Replied', value: stats.replied, icon: Mail, color: 'text-purple-600 bg-purple-50' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-grey-600 bg-grey-50' },
    { label: 'Failed', value: stats.failed, icon: Trash2, color: 'text-red-600 bg-red-50' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {statCards.map((stat) => (
        <Card key={stat.label} padding="md">
          <div className="flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', stat.color)}>
              <stat.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-baseline gap-1.5">
                <p className="text-2xl font-bold text-secondary">{stat.value}</p>
                {stat.subtitle && <span className="text-sm text-grey-400">{stat.subtitle}</span>}
              </div>
              <p className="text-xs text-grey-500">{stat.label}</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
